import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import SellIcon from '@mui/icons-material/Sell';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InventoryIcon from '@mui/icons-material/Inventory';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HelpIcon from '@mui/icons-material/Help';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SettingsIcon from '@mui/icons-material/Settings';

/** Shared style for sidebar nav buttons — takes isCollapsed for layout */
const navButtonSx = (isCollapsed) => ({
  justifyContent: isCollapsed ? 'center' : 'flex-start',
  textTransform: 'none',
  fontSize: '1rem',
  maxWidth: isCollapsed ? 'auto' : '90%',
  color: 'common.white',
  borderColor: 'rgba(255, 255, 255, 0.3)',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
});

const sidebarDividerSx = { borderColor: 'rgba(255, 255, 255, 0.2)' };
const sidebarTextMutedSx = { color: 'rgba(255, 255, 255, 0.7)' };

const Sidebar = ({
  user,
  onManageInventoryClick,
  showChannelManagement,
  onManageChannelsClick,
  showReports,
  onReportsClick,
  onLogout,
  mobileOpen,
  onMobileToggle,
  showAccountManagement,
  onAccountManagementClick,
  showHelpAndSupport,
  onHelpAndSupportClick,
  showImportData,
  onImportDataClick,
  showExportData,
  onExportDataClick,
  showSalesTransactions,
  onSalesTransactionsClick,
  showExpenses,
  onExpensesClick,
  showMileage,
  onMileageClick,
  isCollapsed,
  onToggleCollapsed,
}) => {
  const COLLAPSED_WIDTH = 80;
  const EXPANDED_WIDTH = 280;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mobile drawer always renders expanded (icons + text)
  const effectiveCollapsed = isMobile ? false : isCollapsed;
  const currentWidth = effectiveCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const isInventoryView = !showChannelManagement && !showReports && !showAccountManagement && !showSalesTransactions && !showExpenses && !showMileage && !showHelpAndSupport && !showImportData && !showExportData;

  const handleNav = (callback) => () => {
    callback();
    if (isMobile) onMobileToggle(false);
  };

  const btnSx = navButtonSx(effectiveCollapsed);

  const navItems = [
    { label: 'Manage Inventory', icon: <InventoryIcon />, active: isInventoryView, onClick: handleNav(onManageInventoryClick) },
    { label: 'Manage Channels', icon: <SellIcon />, active: showChannelManagement, onClick: handleNav(onManageChannelsClick) },
    { label: 'Sales Transactions', icon: <MonetizationOnIcon />, active: showSalesTransactions, onClick: handleNav(onSalesTransactionsClick) },
    { label: 'Expenses', icon: <ReceiptIcon />, active: showExpenses, onClick: handleNav(onExpensesClick) },
    { label: 'Mileage Log', icon: <DirectionsCarIcon />, active: showMileage, onClick: handleNav(onMileageClick) },
    { label: 'Reports', icon: <AssessmentIcon />, active: showReports, onClick: handleNav(onReportsClick) },
    { label: 'Import Data', icon: <UploadFileIcon />, active: showImportData, onClick: handleNav(onImportDataClick) },
    { label: 'Export Data', icon: <FileDownloadIcon />, active: showExportData, onClick: handleNav(onExportDataClick) },
    { label: 'Help & Support', icon: <HelpIcon />, active: showHelpAndSupport, onClick: handleNav(onHelpAndSupportClick) },
  ];

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: effectiveCollapsed ? 1.5 : 2,
      }}
    >
      {/* Logo/Title with Collapse Toggle */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: effectiveCollapsed ? 'center' : 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: effectiveCollapsed ? 0 : 1, flex: 1 }}>
          <Box component="img" src="/rp-barcode-icon.svg" alt="" sx={{ height: 24, width: 'auto' }} />
          {!effectiveCollapsed && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'common.white',
                  margin: 0,
                  lineHeight: 1,
                }}
              >
                Reseller Pro
              </Typography>
              <Typography variant="caption" sx={sidebarTextMutedSx}>
                Tools for Resellers
              </Typography>
            </Box>
          )}
        </Box>
        {!effectiveCollapsed && (
          <IconButton
            size="small"
            onClick={onToggleCollapsed}
            sx={{ color: 'common.white' }}
            aria-label="Collapse sidebar"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Collapse button when collapsed */}
      {effectiveCollapsed && (
        <IconButton
          size="small"
          onClick={onToggleCollapsed}
          sx={{ color: 'common.white', mb: 2, alignSelf: 'center' }}
          aria-label="Expand sidebar"
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      )}

      {!effectiveCollapsed && <Divider sx={{ mb: 2, ...sidebarDividerSx }} />}

      {/* Navigation Buttons */}
      <Stack spacing={1} sx={{ flexGrow: 1 }}>
        {navItems.map((item) => (
          <Button
            key={item.label}
            variant={item.active ? 'contained' : 'outlined'}
            startIcon={item.icon}
            onClick={item.onClick}
            sx={btnSx}
          >
            {!effectiveCollapsed && item.label}
          </Button>
        ))}
      </Stack>

      <Divider sx={{ my: 2, ...sidebarDividerSx }} />

      {/* User Section */}
      {user && (
        <Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1, ...sidebarTextMutedSx }}
          >
            ACCOUNT
          </Typography>
          <Button
            variant={showAccountManagement ? 'contained' : 'outlined'}
            startIcon={<SettingsIcon />}
            onClick={handleNav(onAccountManagementClick)}
            sx={{ ...btnSx, mb: 1.5 }}
          >
            {!effectiveCollapsed && 'Account Settings'}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleNav(onLogout)}
            sx={{
              ...btnSx,
              fontSize: '0.95rem',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                borderColor: 'common.white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {!effectiveCollapsed && 'Logout'}
          </Button>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => onMobileToggle(false)}
        ModalProps={{ keepMounted: true }}
        sx={(theme) => ({
          '& .MuiDrawer-paper': {
            width: EXPANDED_WIDTH,
            boxSizing: 'border-box',
            background: `linear-gradient(to bottom, ${theme.palette.sidebar.gradientStart} 0%, ${theme.palette.sidebar.gradientEnd} 100%)`,
            borderRight: `1px solid ${theme.palette.sidebar.gradientEnd}`,
          },
        })}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={(theme) => ({
        width: currentWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          background: `linear-gradient(to bottom, ${theme.palette.sidebar.gradientStart} 0%, ${theme.palette.sidebar.gradientEnd} 100%)`,
          borderRight: `1px solid ${theme.palette.sidebar.gradientEnd}`,
          transition: 'width 0.3s ease',
        },
      })}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
